import React from "react";
import {
  ThemedSiderV2,
} from "@refinedev/mui";
import { Box, Divider, Typography, IconButton, Tooltip } from "@mui/material";
import { ChevronRight } from "lucide-react";
import { SiderFooter } from "./SiderFooter";
import { useTranslation } from "react-i18next";
import { useSidebarCollapsed } from "../../hooks/useSidebarCollapsed";

export const CustomSider: React.FC<React.ComponentProps<typeof ThemedSiderV2>> = (props) => {
  const { t } = useTranslation();
  const { setCollapsed } = useSidebarCollapsed();

  const handleExpandSidebar = () => {
    setCollapsed(false);
  };

  // Effect to manage collapsed state CSS class
  React.useEffect(() => {
    const checkCollapsedState = () => {
      const drawer = document.querySelector('.MuiDrawer-root');
      if (drawer) {
        const drawerPaper = drawer.querySelector('.MuiDrawer-paper');
        if (drawerPaper) {
          // Check if drawer width is collapsed (typically around 64px or 80px)
          const width = drawerPaper.getBoundingClientRect().width;
          const collapsed = width < 120; // Increased threshold for better detection

          if (collapsed) {
            drawerPaper.classList.add('sidebar-collapsed');
          } else {
            drawerPaper.classList.remove('sidebar-collapsed');
          }

        }
      }
    };

    // Initial check with delay to ensure DOM is ready
    setTimeout(checkCollapsedState, 100);

    // Check on resize and mutations
    const observer = new MutationObserver(() => {
      setTimeout(checkCollapsedState, 0);
    });

    const drawer = document.querySelector('.MuiDrawer-root');
    if (drawer) {
      observer.observe(drawer, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        subtree: true,
        childList: true
      });
    }

    // Also listen to window resize
    window.addEventListener('resize', checkCollapsedState);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkCollapsedState);
    };
  }, []);

  return (
    <ThemedSiderV2
      {...props}
      render={({ items, logout, collapsed }) => {
        // Séparer les items en deux catégories
        const mainMenuItems = items.slice(0, 5); // Dashboard, Alertes, Monitoring, Localisation, Lieux partagés
        const managementMenuItems = items.slice(5); // Users, Entreprises, Événements, Code Promos, OTA

        return (
          <Box sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            // Sur mobile, assurer une hauteur appropriée
            '@media (max-width: 768px)': {
              height: '100%',
              maxHeight: '100%',
            }
          }}>
            {/* Bouton pour agrandir la sidebar quand elle est réduite */}
            {collapsed && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  // Masquer sur mobile car on utilise le drawer
                  '@media (max-width: 768px)': {
                    display: 'none',
                  },
                }}
              >
                <Tooltip title={t('sidebar.expand', 'Agrandir la sidebar')} placement="right">
                  <IconButton
                    onClick={handleExpandSidebar}
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ChevronRight size={20} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Box
              sx={{
                flex: "1 1 auto",
                overflow: "auto",
                overflowX: "hidden",
                minHeight: 0, // Important pour permettre le shrink du conteneur
                // Appliquer des styles spécifiques pour le mode collapsed
                '& .MuiList-root .MuiListItemButton-root': collapsed ? {
                  justifyContent: 'center !important',
                  px: '12px !important',
                  minWidth: '48px !important',
                  '& .MuiListItemIcon-root': {
                    minWidth: 'auto !important',
                    mr: '0 !important',
                  },
                  '& .MuiListItemText-root': {
                    display: 'none !important',
                  },
                } : {},
              }}
            >
              {/* Première catégorie - Menu principal */}
              <Box sx={{ mb: 2 }}>
                {!collapsed && managementMenuItems.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2,
                      py: 1,
                      display: 'block',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {t('sidebar.sections.principal')}
                  </Typography>
                )}
                {mainMenuItems}
              </Box>

              {/* Divider - only show if management section has items */}
              {managementMenuItems.length > 0 && (
                <Divider sx={{ mx: 2, my: 1 }} />
              )}

              {/* Deuxième catégorie - Gestion */}
              {managementMenuItems.length > 0 && (
                <Box>
                  {!collapsed && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 2,
                        py: 1,
                        display: 'block',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        color: 'text.secondary',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {t('sidebar.sections.management')}
                    </Typography>
                  )}
                  {managementMenuItems}
                </Box>
              )}
            </Box>
            <Box sx={{
              flexShrink: 0,
              mt: 'auto', // Pousser le footer vers le bas
              // Sur mobile, ajouter un padding pour la safe area iOS
              '@media (max-width: 768px)': {
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              }
            }}>
              <SiderFooter collapsed={collapsed} />
            </Box>
          </Box>
        );
      }}
    />
  );
};
